import React from 'react';

import { type HexAlpha, type UUID, isDefined, isEmpty, silenceError } from '@nfctron/nexus-core';
import { type ExtendedComponent, cn, useContextRequired, useFormat } from '@nfctron/nexus-react';
import { BagIcon } from '@nfctron/nexus-react-svg';

import { ActionIcon } from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';
import { CircleDollarSignIcon, GiftIcon, SendIcon, ShoppingCartIcon, TicketIcon, TrashIcon, TreeDeciduousIcon, WalletIcon } from 'lucide-react';

import { getEventTypeSensitiveComponentDisplayName } from '@root/pages/event/[organizer]/[event]/_resources/helpers/common';

import { CartStoreMaximusSlice, useCartStore, useCartStoreCtx } from '@root/cart/cart/_resources/stores/cartStore';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { useTracking } from '@root/lib/modules/tracking/TrackingProvider';
import { type SvgIconComponent } from '@root/lib/types/components';

import { EventCartPageContext } from '../CartContext';

import { TicketCardDivider } from './TicketCardDivider';

/**
 * TicketCardCarted/Item component
 * @constructor
 */
export const TicketCardCartedItem: React.FunctionComponent<
	ExtendedComponent<{
		Icon?: SvgIconComponent;
		value: React.ReactNode;
		price: React.ReactNode;
		originalPrice?: React.ReactNode;
		level: 1 | 2;
		count?: number;
	}>
> = ({ Icon, value, count, price, level, className, originalPrice, ...props }) => {
	const format = useFormat();
	return (
		<div className={cn(['flex items-center gap-3 text-base text-zinc-700', level === 2 && 'pl-3', className])} {...props}>
			<div className="flex w-full items-center gap-1.5 truncate">
				{/* icon */}
				{isDefined(Icon) && <Icon className="inline size-4" />}
				{/* value */}
				<span className="w-full truncate">
					{isDefined(count) && count > 1 && <span>{format.number(count)}&times;&nbsp;</span>}
					{value}
				</span>
			</div>
			{/* price */}
			<div className="flex w-1/3 items-center justify-end gap-1 text-right">
				{/* original price */}
				{isDefined(originalPrice) && <span className="text-xs text-zinc-500 line-through">{originalPrice}</span>}
				{/* current price */}
				<span className="text-lucky-point-500">{price}</span>
			</div>
		</div>
	);
};

/**
 * Event/Cart/TicketCardCarted component
 * @constructor
 */
export const TicketCardCarted: React.FunctionComponent<TicketCardCartedProps> = ({ cartTicketId, dividerBgColor = '#FFF', ...props }) => {
	const { event } = useContextRequired(EventCartPageContext);
	const format = useFormat();
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, TicketCardCarted.displayName);
	const { t: typeSensitiveT } = useTranslate(
		APP_I18N_NAMESPACE.MAIN,
		getEventTypeSensitiveComponentDisplayName(TicketCardCarted.displayName, event.eventType.eventTypeId),
	);

	const cartStoreCtx = useCartStoreCtx();
	const getExtraProductById = cartStoreCtx.useGetExtraProductById();
	const { trackingModule } = useTracking();
	const { ticket, ticketType: ticketTypeConfig } = useCartStore((s) => s.getCartedTicket({ cartTicketId }));
	const removeCartedTickets = useCartStore((s) => s.removeCartedTickets);
	const getCartedTicketHolderNameFallback = useCartStore((s) => s.getCartedTicketHolderNameFallback);
	const ticketMaximusProducts = useCartStore((s) => s.getCartedMaximusProductsForCartedTicket({ cartTicketId }), 'deep');
	const cartedTicketTotal = useCartStore((s) => s.getCartedTicketTotal({ cartTicketId }));
	const cartedTicketMaximusTotal = useCartStore((s) => s.getCartedMaximusProductsTotal({ cartTicketId }));
	const seat = useCartStore((s) => s.getSeatFromCartedTicket({ cartTicketId }));
	const crowd = useCartStore((s) => s.getCrowdFromCartedTicket({ cartTicketId }));
	const getSectorById = useCartStore((s) => s.getSectorById);
	const sector = React.useMemo(() => {
		return silenceError(() => getSectorById({ sectorId: isDefined(crowd) ? crowd.sectorId : isDefined(seat) ? seat.sectorId : '' }), null);
	}, [getSectorById, crowd, seat]);

	return (
		<div {...props} className={cn(['flex flex-col justify-between rounded-xl border border-gray-200 bg-gray-50 pb-2', props.className])}>
			{/* ticket header */}
			<header className="flex items-start justify-between gap-3 p-4 pb-2">
				{!isEmpty(ticket.holderName) ? (
					<div>
						{/* ticket holder name */}
						<span className="block text-base text-zinc-500">{typeSensitiveT('holderName.text')}</span>
						<span className="text-lucky-point-500 block text-xl font-bold">{ticket.holderName}</span>
					</div>
				) : (
					<div>
						{/* ticket name */}
						<span className="block text-base text-zinc-500">{typeSensitiveT('ticketType.text')}</span>
						<span className="text-lucky-point-500 block text-xl font-bold">
							{getCartedTicketHolderNameFallback({ cartTicketId: ticket.cartTicketId })}
						</span>
					</div>
				)}

				{/* delete icon button */}
				{ticketTypeConfig.extraData.addToCartQty === 1 && ticketTypeConfig.__isListable && (
					<ActionIcon
						variant="transparent"
						radius="xl"
						color="red"
						onClick={() => {
							openConfirmModal({
								title: typeSensitiveT('removeConfirmation.title'),
								centered: true,
								children: <span className="text-base">{typeSensitiveT('removeConfirmation.text')}</span>,
								labels: { confirm: t('removeConfirmation.confirmButton.text'), cancel: t('removeConfirmation.cancelButton.text') },
								confirmProps: { color: 'pink' },
								cancelProps: { color: 'zinc', variant: 'light' },
								onConfirm: async () => {
									removeCartedTickets({ cartTicketIds: [cartTicketId], trackingModule });
								},
							});
						}}
					>
						<TrashIcon size={19} />
					</ActionIcon>
				)}
			</header>

			{/* extra data */}
			{isDefined(ticketTypeConfig.userData) && ticketTypeConfig.userData.fields.length > 0 && (
				<React.Fragment>
					{/* divider */}
					<TicketCardDivider bgColor={dividerBgColor} />

					<section className="flex grow flex-col px-4 py-2">
						{ticketTypeConfig.userData.fields.map((field) => (
							<div key={field.fieldId} className={cn(['flex items-center gap-3'])}>
								{/* label */}
								<span className="w-full max-w-1/2 truncate text-left text-base text-zinc-500">{field.label}</span>
								{/* value */}
								<span className="text-lucky-point-500 w-1/2 grow truncate text-right text-base">{ticket.userData?.[field.fieldId] ?? '--'}</span>
							</div>
						))}
					</section>
				</React.Fragment>
			)}

			{/* sector section */}
			{isDefined(sector) && (
				<React.Fragment>
					{/* divider */}
					<TicketCardDivider bgColor={dividerBgColor} />

					{/* seats section */}
					<section className="flex grow flex-col gap-4 px-4 py-2">
						<div className="grid grid-cols-3 gap-3">
							{/* sector */}
							<div>
								<span className="block text-base text-zinc-500">{t('seating.sector.label')}</span>
								<span className="text-lucky-point-500 block text-xl">{isDefined(sector) ? sector.label : '--'}</span>
							</div>
							{/* crowd */}
							{isDefined(crowd) ? (
								<div className="col-span-2">
									<span className="block text-base text-zinc-500">{t('seating.crowd.label')}</span>
									<span className="text-lucky-point-500 block text-xl">{crowd.name}</span>
								</div>
							) : (
								<React.Fragment>
									{/* row */}
									<div>
										<span className="block text-base text-zinc-500">{t('seating.seatRow.label')}</span>
										<span className="text-lucky-point-500 block text-xl">{isDefined(seat) ? seat.seatRow : '--'}</span>
									</div>
									{/* seat */}
									<div>
										<span className="block text-base text-zinc-500">{t('seating.seatPlace.label')}</span>
										<span className="text-lucky-point-500 block text-xl">{isDefined(seat) ? seat.label : '--'}</span>
									</div>
								</React.Fragment>
							)}
						</div>
					</section>
				</React.Fragment>
			)}

			{/* divider */}
			<TicketCardDivider bgColor={dividerBgColor} />

			{/* ticket content section */}
			<section className="flex grow flex-col gap-1 px-4 py-2">
				{/* ticket */}
				<TicketCardCartedItem
					Icon={TicketIcon}
					level={1}
					className={cn([ticketTypeConfig.__isImported && 'line-through'])}
					value={ticketTypeConfig.namePub}
					price={format.price(ticketTypeConfig.price, event.currency.baseCurrency.currencyId)}
					originalPrice={
						cartedTicketTotal.ticket !== cartedTicketTotal.ticketOriginal
							? format.price(cartedTicketTotal.ticketOriginal, event.currency.baseCurrency.currencyId)
							: undefined
					}
				/>
				{/* chips */}
				{ticket.chips.map((chip) => {
					/** if not charged, hide */
					if (chip.amount <= 0) return null;

					return (
						<TicketCardCartedItem
							key={chip.cartChipId}
							value={
								<span>
									<span>{t('creditTopUp.text')}</span>
									{isDefined(event.currency.virtualCurrency) && (
										<span className="text-md ml-1">({format.virtualPrice(chip.amount, event.currency.virtualCurrency)})</span>
									)}
								</span>
							}
							price={format.price(chip.amount, event.currency.baseCurrency.currencyId)}
							Icon={WalletIcon}
							level={2}
						/>
					);
				})}
				{/* maximus products */}
				{ticketMaximusProducts.map(({ maximusProductConfig, maximusProduct }) => {
					const Icon = {
						[CartStoreMaximusSlice.MAXIMUS_PRODUCT_TYPE.TICKET_GIFT_WRAP]: GiftIcon,
						[CartStoreMaximusSlice.MAXIMUS_PRODUCT_TYPE.TICKET_GIFT_WRAP_DELIVERY]: SendIcon,
						[CartStoreMaximusSlice.MAXIMUS_PRODUCT_TYPE.TREE_DONATION]: TreeDeciduousIcon,
						[CartStoreMaximusSlice.MAXIMUS_PRODUCT_TYPE.TICKET_PROCESSING_FEE]: CircleDollarSignIcon,
						[CartStoreMaximusSlice.MAXIMUS_PRODUCT_TYPE.PAYMENT_PROCESSING_FEE]: CircleDollarSignIcon,
					}[maximusProductConfig.productTypeId];
					return (
						<TicketCardCartedItem
							level={2}
							Icon={Icon}
							key={maximusProduct.maximusProductId}
							value={maximusProductConfig.namePub}
							price={format.price(maximusProduct.count * maximusProductConfig.price, event.currency.baseCurrency.currencyId)}
						/>
					);
				})}
				{/* extra products */}
				{ticket.extraProducts.map((extraProduct) => {
					const extraProductConfig = getExtraProductById({ ticketProductId: extraProduct.ticketProductId });
					return (
						<React.Fragment key={extraProduct.cartProductId}>
							{/* extra product */}
							<TicketCardCartedItem
								value={
									<span>
										{/* extra product name */}
										{extraProductConfig.name}
										{/* extra product optional text (if any) */}
										{!isEmpty(extraProduct.optionalText) && (
											<React.Fragment>
												{' '}
												<span className="text-md text-gray-500">– {extraProduct.optionalText}</span>
											</React.Fragment>
										)}
									</span>
								}
								count={extraProduct.count}
								price={format.price(extraProduct.count * extraProductConfig.price, event.currency.baseCurrency.currencyId)}
								Icon={BagIcon}
								level={1}
							/>
							{/* extra product chips */}
							{extraProduct.chips.map(
								(chip) =>
									chip.amount > 0 && (
										<TicketCardCartedItem
											key={chip.cartChipId}
											value={
												<span>
													<span>{t('creditTopUp.text')}</span>
													{isDefined(event.currency.virtualCurrency) && (
														<span className="text-md ml-1">({format.virtualPrice(chip.amount, event.currency.virtualCurrency)})</span>
													)}
												</span>
											}
											price={format.price(chip.amount, event.currency.baseCurrency.currencyId)}
											Icon={WalletIcon}
											level={2}
										/>
									),
							)}
						</React.Fragment>
					);
				})}
			</section>

			{/* divider */}
			<TicketCardDivider bgColor={dividerBgColor} />

			{/* total */}
			<section className="flex flex-col gap-1 px-4 py-2">
				<TicketCardCartedItem
					value={typeSensitiveT('totalPrice.text')}
					price={format.price(cartedTicketTotal.total + cartedTicketMaximusTotal.total, event.currency.baseCurrency.currencyId)}
					Icon={ShoppingCartIcon}
					level={1}
					className="text-lucky-point-500 text-lg font-semibold"
				/>
			</section>
		</div>
	);
};

/**
 * Event/Cart/TicketCardCarted component props type
 * @export
 */
export type TicketCardCartedProps = ExtendedComponent<{ cartTicketId: UUID; dividerBgColor?: HexAlpha }>;

TicketCardCarted.displayName = 'Event/Cart/TicketCardCarted';
